import BaseDialog from 'components/dialog'
import React, { useCallback, useEffect, useState } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client'
import IconTimes from 'assets/images/icon-times.svg'
import ButtonBase from 'components/partials/button'
import {
  ADD_OR_EDIT_AGENCY_TO_AGENCY_GROUP,
  GET_AGENCIES_BY_AGENCY_GROUP_ID,
  IAgency,
  SEARCH_AGENCIES_BY_AGENCY_NAME,
} from 'queries/admin/agency'
import { useDebounceValue } from 'usehooks-ts'
import { IAgencyGroup } from '../../interface'
import Loading from 'components/loading'
import { notify } from 'components/toastify'
import { STATUS } from 'constants/messages'
import { useSession } from 'helpers/checkSession'

interface Props {
  currentAgencyGroup: IAgencyGroup
  isOpenDialog: boolean
  handleCloseDialog: () => void
}

const styleDialogDefault: ReactModal.Styles = {
  content: {
    width: '100%',
    maxWidth: '1200px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    borderRadius: '16px',
    boxShadow: '8px 11px 21px 0px rgba(148,144,144,0.75)',
    background: '#fdfdfd',
    maxHeight: '700px',
    overflow: 'auto',
  },
}

const AddAgenciesToAgencyGroup: React.FC<Props> = ({
  handleCloseDialog,
  isOpenDialog,
  currentAgencyGroup,
}) => {
  const { handleErrorSession } = useSession()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 500)
  const [selected, setSelected] = useState<IAgency[]>([])
  const [agencies, setAgencies] = useState<IAgency[]>([])
  const [skip, setSkip] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const take = 10

  // Lazy Query Hook
  const [getAgencies, { loading: queryLoading }] = useLazyQuery(SEARCH_AGENCIES_BY_AGENCY_NAME, {
    onCompleted: (data) => {
      if (data.searchAgencyByAgencyName.length < take) {
        setHasMore(false)
      }

      setAgencies((prev) =>
        skip === 0 ? data.searchAgencyByAgencyName : [...prev, ...data.searchAgencyByAgencyName],
      )

      setLoading(false)
      setError(null)
    },
    onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
        setError('Failed to load agencies')
      }
      // setError('Failed to load agencies')
      setLoading(false)
    },
  })

  const [getAgenciesByAgencyGroupQuery] = useLazyQuery(GET_AGENCIES_BY_AGENCY_GROUP_ID, {
    variables: {
      agencyGroupId: currentAgencyGroup.agencyGroupId,
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (!data) return
      setSelected(data.getAgenciesByAgencyGroupId)
    },
    onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
  })

  // const [getSelectedAgencies]

  const [addAgencyToAgencyGroup] = useMutation(ADD_OR_EDIT_AGENCY_TO_AGENCY_GROUP, {
    variables: {
      data: {
        agencyGroupId: currentAgencyGroup.agencyGroupId,
        agencyIds: selected.map((item) => item.agencyId),
      },
    },
    onCompleted: (data) => {
      if (data.addOrEditAgencyToAgencyGroup) {
        notify('Add agency to agency group successfully', STATUS.SUCCESS)
        handleCloseDialog()
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
    onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
  })

  // Fetch advertisers based on debounced search and page
  const fetchAgenciesBySearchTerm = useCallback(() => {
    setLoading(true)
    getAgencies({
      variables: {
        skip,
        take,
        agencyName: searchTerm,
        agencyGroupId: currentAgencyGroup.agencyGroupId,
        includingAgencyIds: selected.map((item) => item.agencyId),
        isFetchAll: selected.length === 0,
      },
      fetchPolicy: 'cache-and-network',
    })
  }, [skip, debouncedSearchTerm, getAgencies])

  useEffect(() => {
    fetchAgenciesBySearchTerm()
  }, [fetchAgenciesBySearchTerm])

  useEffect(() => {
    if (error) {
      notify(error, STATUS.ERROR)
    }
  }, [error])

  useEffect(() => {
    getAgenciesByAgencyGroupQuery()
  }, [])

  const handleSearch = (value: string) => {
    setSkip(0) // Reset to first page on new search
    setSearchTerm(value)
  }

  const handleToggle = (agency: IAgency) => {
    setSelected((prev) =>
      prev.some((item) => item.agencyId === agency.agencyId)
        ? prev.filter((item) => item.agencyId !== agency.agencyId)
        : [...prev, agency],
    )
  }

  const removeSelected = (agency: IAgency) => {
    setSelected((prev) => prev.filter((item) => item.agencyId !== agency.agencyId))
  }

  const loadMore = () => {
    if (!loading) setSkip((prev) => prev + take)
  }

  const onSaveBtn = () => {
    addAgencyToAgencyGroup()
  }

  const onClickClearBtn = () => {
    setSelected([])
  }

  return (
    <BaseDialog modalIsOpen={isOpenDialog} customStyles={styleDialogDefault}>
      <div className="flex flex-col max-h-[600px] overflow-y-scroll overflow-x-hidden px-[5px]">
        <div className="flex justify-between">
          <div />
          <img src={IconTimes} alt="" className="cursor-pointer " onClick={handleCloseDialog} />
        </div>
        <div className="flex justify-center pb-5">
          <p className="text-headline5A text-neutral-1">Add agencies to agency group</p>
        </div>

        {/* <AgenciesSelector/> */}
        {loading && skip === 0 && <Loading className="relative p-[5px]" height={30} width={30} />}
        <div className="w-full flex space-x-2">
          <div className="w-1/2">
            <div className="w-full flex justify-between">
              <h3 className="text-lg font-semibold">Agencies</h3>
              <span>Selected: {selected.length}</span>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search agency"
              className="w-full h-[40px] mb-[10px] border border-gray-100 px-2"
            />
            <div className="border border-gray-300 rounded max-h-[350px] overflow-y-auto">
              <ul className="list-none p-0 m-0">
                {agencies.map((agency) => (
                  <li
                    key={agency.agencyId}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).tagName === 'INPUT') {
                        e.stopPropagation()
                        return
                      }
                      handleToggle(agency)
                    }}
                    className="flex items-center p-2 cursor-pointer hover:bg-gray-100"
                  >
                    <input
                      type="checkbox"
                      checked={selected.some((item) => item.agencyId === agency.agencyId)}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleToggle(agency)
                      }}
                      className="mr-2"
                    />
                    <span>{agency.agencyName}</span>
                  </li>
                ))}
              </ul>
              {queryLoading && <div className="p-2 text-center">Loading more...</div>}

              {/* {!queryLoading && agencies.length % take === 0 && ( */}
              {!queryLoading && hasMore && (
                <div className="p-2 text-center cursor-pointer text-blue-500" onClick={loadMore}>
                  Load More
                </div>
              )}
            </div>
          </div>

          <div className="w-1/2">
            <div className="w-full flex justify-between">
              <h3 className="text-lg font-semibold">Selected Agencies</h3>
              <button onClick={onClickClearBtn}>Clear</button>
            </div>
            <div className="border border-gray-300 rounded h-[400px] max-h-[400px] overflow-y-auto">
              <ul className="list-none p-0 mt-2 pl-2">
                {selected.map((agency) => (
                  <li
                    key={`selected-${agency.agencyId}`}
                    className="flex justify-between px-2 py-1 cursor-pointer hover:bg-gray-100"
                    onClick={() => removeSelected(agency)}
                  >
                    <span>{agency.agencyName}</span>
                    <span className="text-red-500">x</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-between">
          <ButtonBase
            onClick={onSaveBtn}
            className="w-[150px] my-4 justify-around"
            // disabled={error.isError}
            bgDisabled="bg-primary-shade3"
          >
            Save
          </ButtonBase>
          <ButtonBase
            type="submit"
            className="w-[150px] my-4 justify-around border-2 border-primary-1 text-primary-1 bg-white-50 hover:bg-primary-shade4"
            onClick={handleCloseDialog}
            bgDisabled="bg-primary-shade3"
          >
            Close
          </ButtonBase>
        </div>
      </div>
    </BaseDialog>
  )
}

export default AddAgenciesToAgencyGroup
